import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useJobIntelProfile } from '@/hooks/useJobIntelProfile';
import { ProfileSetupPathSelector } from '@/components/JobIntelProfile/ProfileSetupPathSelector';
import { DocumentUploadPath } from '@/components/JobIntelProfile/DocumentUploadPath';
import { ManualProfileForm } from '@/components/JobIntelProfile/ManualProfileForm';
import { ProfileCompletionBadge } from '@/components/JobIntelProfile/ProfileCompletionBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowRight, Sparkles } from 'lucide-react';
import type { SetupMethod, JobIntelUserProfile } from '@/types/jobintelProfile';

export default function JobIntelProfileSetup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading, saving, saveProfile, completionPercentage } = useJobIntelProfile();
  const [selectedPath, setSelectedPath] = useState<SetupMethod | null>(null);
  const [fieldMeta, setFieldMeta] = useState<Record<string, { confidence: string; source?: string }>>({});
  const [prefillData, setPrefillData] = useState<Partial<JobIntelUserProfile>>({});
  const [showReview, setShowReview] = useState(false);
  const [showCareerClarityPrompt, setShowCareerClarityPrompt] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If profile exists and is being edited, go straight to manual form
  const initialData = profile ? { ...profile, ...prefillData } : prefillData;

  const handlePathSelect = (method: SetupMethod) => {
    setSelectedPath(method);
    if (method === 'manual') {
      setPrefillData(profile || {});
    } else if (method === 'career_clarity_report') {
      // TODO: Check for existing Career Clarity report and pre-fill
      setPrefillData(profile || {});
    } else if (method === 'tailorwiz_profile') {
      // TODO: Pull TailorWiz profile data
      setPrefillData(profile || {});
    }
  };

  const handleDocumentParseComplete = (data: Partial<JobIntelUserProfile>, meta: Record<string, { confidence: string; source?: string }>) => {
    setPrefillData(prev => ({ ...prev, ...data }));
    setFieldMeta(meta);
    setSelectedPath('manual'); // Go to form to review/complete
  };

  const handleFormComplete = async (data: Partial<JobIntelUserProfile>) => {
    const setupMethod = selectedPath || 'manual';
    const result = await saveProfile({ ...data, setup_method: setupMethod });
    if (result) {
      if (setupMethod !== 'career_clarity_report') {
        setShowCareerClarityPrompt(true);
      } else {
        setShowReview(true);
      }
    }
  };

  const handleSaveAndContinue = () => {
    navigate('/');
  };

  const handleSaveAndSetupAlerts = () => {
    navigate('/job-finder-hub');
  };

  // Career Clarity prompt screen
  if (showCareerClarityPrompt) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center">
            <div className="mx-auto p-3 rounded-full bg-[hsl(var(--primary-light))] w-fit mb-2">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-xl">Want the full picture?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-muted-foreground">
              Run your Career Clarity Report and we'll automatically update your profile with real market intelligence — the roles you're most competitive for and the companies that are the best fit.
            </p>
            <p className="text-sm text-muted-foreground">This usually takes about 2-3 minutes.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Button variant="outline" onClick={() => setShowReview(true)}>
                Skip for now
              </Button>
              <Button onClick={() => {
                // TODO: Trigger Career Clarity Report
                setShowReview(true);
              }} className="bg-[hsl(var(--primary))]">
                <Sparkles className="h-4 w-4 mr-2" />
                Run Career Clarity Report
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Final review screen
  if (showReview) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="text-center space-y-3">
            <ProfileCompletionBadge percentage={completionPercentage} size="lg" />
            <h1 className="text-2xl font-bold text-foreground">Your Career Profile is Ready!</h1>
            <p className="text-muted-foreground">
              {completionPercentage < 80
                ? 'Your profile is saved. Come back anytime to complete more fields for better results.'
                : 'Your profile is comprehensive and ready to power all your JobIntel 360 reports.'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
            <Button variant="outline" onClick={handleSaveAndContinue}>
              Go to Dashboard
            </Button>
            <Button onClick={handleSaveAndSetupAlerts} className="bg-[hsl(var(--primary))]">
              Save & Set Up Job Alerts <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Path selection or active path
  return (
    <div className="min-h-screen bg-background p-6">
      {!selectedPath && (
        <ProfileSetupPathSelector
          onSelect={handlePathSelect}
          hasTailorWizProfile={!!profile?.current_job_title}
          hasCareerClarityReport={!!profile?.career_clarity_report_id}
        />
      )}

      {selectedPath === 'documents_uploaded' && (
        <DocumentUploadPath
          onComplete={handleDocumentParseComplete}
          onBack={() => setSelectedPath(null)}
        />
      )}

      {(selectedPath === 'manual' || selectedPath === 'career_clarity_report' || selectedPath === 'tailorwiz_profile') && (
        <ManualProfileForm
          initialData={initialData}
          onComplete={handleFormComplete}
          onBack={() => setSelectedPath(null)}
          fieldMeta={Object.keys(fieldMeta).length > 0 ? fieldMeta : undefined}
        />
      )}
    </div>
  );
}
